import { useState } from 'react';

import { Box, Group, Select } from '@mantine/core';

import { IconChevronDown } from '@tabler/icons-react';

import { SidepanelHeader } from '../../../shared/components/UI/Sidepanel/Sidepanel';

import type { TengoDocument } from '../../../shared/entities/TengoDocument';
import { DownloadFileButton } from './DownloadFileButton';
import { DocumentPreview } from './preview/DocumentPreview';

const DOCUMENT_SIDE_PANEL_WIDTH = '50vw';
const DOCUMENT_SIDE_PANEL_MAX_WIDTH = 800;

export function DocumentSidePanel<T extends TengoDocument>({
  documents,
  selectedDocument,
  closePanel,
}: {
  documents: T[];
  selectedDocument: T;
  closePanel: () => void;
}) {
  const [displayedDocument, setDisplayedDocument] =
    useState<T>(selectedDocument);

  function handleDocumentChange(value: string) {
    const newSelectedDocument = documents.find(
      document => document.id.toString() === value,
    );

    if (!newSelectedDocument) {
      throw new Error('Selected document not found');
    }

    setDisplayedDocument(newSelectedDocument);
  }

  return (
    <>
      <SidepanelHeader
        closePanel={closePanel}
        header={
          <Header
            documents={documents}
            selectedDocument={displayedDocument}
            handleDocumentChange={handleDocumentChange}
          />
        }
      />
      <Content selectedDocument={displayedDocument} />
    </>
  );
}

function Header<T extends TengoDocument>({
  documents,
  selectedDocument,
  handleDocumentChange,
}: {
  documents: T[];
  selectedDocument: T;
  handleDocumentChange: (value: string) => void;
}) {
  const selectData = documents.map(document => ({
    value: document.id.toString(),
    label: document.prettyName,
  }));
  return (
    <Group noWrap position="apart" w="100%" spacing={0}>
      <Group noWrap position="center" align="center" w="100%">
        <Select
          styles={theme => ({
            root: { minWidth: '450px' },
            input: {
              border: 'none',
              textAlign: 'center',
              fontWeight: 500,
              color: theme.colors.gray[9],
              fontSize: theme.fontSizes.sm,
            },
            dropdown: { minWidth: 'fit-content' },
            item: { wordSpacing: 'nowrap' },
            rightSection: {
              pointerEvents: 'none',
            },
          })}
          rightSection={<IconChevronDown size={16} />}
          value={selectedDocument.id.toString()}
          data={selectData}
          onChange={handleDocumentChange}
        />
      </Group>

      <DownloadFileButton
        S3FileURL={selectedDocument.S3FileURL}
        prettyName={selectedDocument.prettyName}
      />
    </Group>
  );
}

function Content<T extends TengoDocument>({
  selectedDocument,
}: {
  selectedDocument: T;
}) {
  return (
    <Box
      px="04"
      w={DOCUMENT_SIDE_PANEL_WIDTH}
      maw={DOCUMENT_SIDE_PANEL_MAX_WIDTH}
      h="100%"
    >
      <DocumentPreview
        initialPage={1}
        S3PreviewFileURL={selectedDocument.S3PreviewFileURL}
        prettyName={selectedDocument.prettyName}
      />
    </Box>
  );
}
